import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  AlertTriangle, Target, TrendingUp, MapPin, Users,
  RefreshCw, Zap, Clock, CheckCircle2, Star,
  Calendar, Award, ChevronRight, Loader2, UserCheck,
  UserX, BarChart2, GitMerge
} from 'lucide-react';
import API_BASE_URL from '../apiConfig';

// ─── Styling maps ────────────────────────────────────────────────────────────

const URGENCY_CARD = {
  high:   'border-l-4 border-red-400   bg-red-50',
  medium: 'border-l-4 border-amber-400 bg-amber-50',
  low:    'border-l-4 border-blue-400  bg-blue-50',
};

const URGENCY_BADGE = {
  high:   'bg-red-100   text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low:    'bg-blue-100  text-blue-700',
};

const TYPE_ICONS = {
  urgent_action:    AlertTriangle,
  territory_alert:  MapPin,
  opportunity:      Zap,
  pattern_detected: TrendingUp,
  goal_progress:    Target,
  daily_focus:      Star,
  team_alert:       Users,
};

const ACTION_VIEW = {
  leads_at_risk:  'list',
  open_visits:    'visits',
  open_territory: 'map',
  open_graph:     'graph',
};

const isManager = (role) => role === 'area_manager' || role === 'hq_manager';

// ─── Sub-components ───────────────────────────────────────────────────────────

function InsightCard({ card, onAction }) {
  const Icon    = TYPE_ICONS[card.type] || Star;
  const urgency = card.urgency || 'medium';
  const view    = ACTION_VIEW[card.action_type];

  return (
    <div className={`rounded-xl p-4 shadow-sm ${URGENCY_CARD[urgency]}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0">
          <Icon size={18} className={
            urgency === 'high' ? 'text-red-500' :
            urgency === 'low'  ? 'text-blue-500' : 'text-amber-500'
          } />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-gray-900 text-sm">{card.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY_BADGE[urgency]}`}>
              {urgency}
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{card.body}</p>
          {card.action_label && view && (
            <button
              onClick={() => onAction(view)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-gray-700
                         hover:text-gray-900 hover:underline transition-colors"
            >
              {card.action_label}
              <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function KPICard({ label, value, sub, color, icon: Icon }) {
  const colors = {
    blue:   'from-blue-50   to-blue-100   border-blue-200   text-blue-700',
    green:  'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700',
    amber:  'from-amber-50  to-amber-100  border-amber-200  text-amber-700',
    red:    'from-red-50    to-red-100    border-red-200    text-red-700',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    gray:   'from-gray-50   to-gray-100   border-gray-200   text-gray-700',
  };
  const cls = colors[color] || colors.gray;

  return (
    <div className={`bg-gradient-to-br ${cls} rounded-xl p-4 border`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon size={14} className="opacity-50" />}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function InsightSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {[1, 2, 3].map(i => (
        <div key={i} className="rounded-xl p-4 border-l-4 border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <div className="w-5 h-5 bg-gray-200 rounded mt-0.5" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-3 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function KPISkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 animate-pulse">
      {[1,2,3,4,5,6].map(i => (
        <div key={i} className="rounded-xl p-4 bg-gray-100 border border-gray-200 h-20" />
      ))}
    </div>
  );
}

function RelativeTime({ iso }) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1)  return <span>just now</span>;
  if (mins < 60) return <span>{mins}m ago</span>;
  const hrs = Math.round(mins / 60);
  if (hrs < 24)  return <span>{hrs}h ago</span>;
  return <span>{Math.round(hrs / 24)}d ago</span>;
}

// ─── Team Coverage sub-components ────────────────────────────────────────────

function CoverageBar({ pct }) {
  const color = pct >= 60 ? 'bg-emerald-500' : pct >= 30 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 w-9 text-right">{pct}%</span>
    </div>
  );
}

function RepCoverageRow({ rep }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${rep.is_inactive ? 'bg-red-50' : 'bg-white'} border border-gray-100`}>
      <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-gray-600">
          {(rep.full_name || rep.email || '?')[0].toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-sm font-medium text-gray-900 truncate">{rep.full_name || rep.email}</span>
          {rep.is_inactive && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold flex-shrink-0">
              inactive
            </span>
          )}
        </div>
        <CoverageBar pct={rep.coverage_pct} />
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-bold text-gray-900">{rep.visits_month}</div>
        <div className="text-[10px] text-gray-400">visits</div>
      </div>
    </div>
  );
}

function CoverageSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-14 bg-gray-100 rounded-lg border border-gray-100" />
      ))}
    </div>
  );
}

// ─── Team Profiles sub-components ────────────────────────────────────────────

function SimilarityBar({ value, label, color = 'indigo' }) {
  const pct = value != null ? Math.round(value * 100) : null;
  const colors = {
    indigo: 'bg-indigo-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-400',
  };
  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <span className="w-24 shrink-0">{label}</span>
      {pct != null ? (
        <>
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${colors[color] || colors.indigo}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="w-8 text-right font-medium text-gray-600">{pct}%</span>
        </>
      ) : (
        <span className="flex-1 text-gray-400 italic">No profile yet</span>
      )}
    </div>
  );
}

function RepProfileRow({ rep }) {
  return (
    <div className={`rounded-xl border p-3 bg-white ${rep.is_top_performer ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
            rep.is_top_performer ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {(rep.full_name || rep.email).charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-800 flex items-center gap-1">
              {rep.full_name || rep.email}
              {rep.is_top_performer && (
                <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                  Top
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400">{rep.visits_30d} visits this month</div>
          </div>
        </div>
        {!rep.has_profile && (
          <span className="text-[10px] text-gray-400 italic">Building profile…</span>
        )}
      </div>
      {rep.has_profile && (
        <div className="space-y-1 mt-1">
          <SimilarityBar value={rep.cohesion_score}    label="Team fit"     color="indigo" />
          <SimilarityBar value={rep.similarity_to_top} label="Like top rep" color="emerald" />
        </div>
      )}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-20 bg-gray-100 rounded-xl border border-gray-100" />
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const POLL_MS = 60_000; // silent KPI poll interval

const DashboardView = ({ currentUser, onViewChange }) => {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [error,         setError]         = useState(null);
  const [coverage,      setCoverage]      = useState(null);
  const [covLoading,    setCovLoading]    = useState(false);
  const [profiles,      setProfiles]      = useState(null);
  const [profLoading,   setProfLoading]   = useState(false);
  const [lastKpiUpdate, setLastKpiUpdate] = useState(null);
  const pollRef = useRef(null);

  const fetchDashboard = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    else              setLoading(true);
    setError(null);

    try {
      const endpoint = forceRefresh
        ? `${API_BASE_URL}/api/dashboard/refresh`
        : `${API_BASE_URL}/api/dashboard/summary`;

      const res = forceRefresh
        ? await axios.post(endpoint)
        : await axios.get(endpoint);

      setData(res.data);
      setLastKpiUpdate(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard. Check your connection.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Silent background poll — updates KPIs only, no loading states, no insight flicker
  const silentPoll = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/dashboard/summary`);
      setData(prev => prev ? { ...prev, kpis: res.data.kpis } : res.data);
      setLastKpiUpdate(new Date());
    } catch { /* silent — don't surface background poll errors */ }
    if (isManager(currentUser?.role)) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/manager/team-coverage`);
        setCoverage(res.data);
      } catch { /* silent */ }
    }
  }, [currentUser?.role]);

  useEffect(() => { fetchDashboard(false); }, [fetchDashboard]);

  useEffect(() => {
    pollRef.current = setInterval(silentPoll, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [silentPoll]);

  useEffect(() => {
    if (!isManager(currentUser?.role)) return;
    setCovLoading(true);
    setProfLoading(true);
    axios.get(`${API_BASE_URL}/api/manager/team-coverage`)
      .then(res => setCoverage(res.data))
      .catch(() => setCoverage(null))
      .finally(() => setCovLoading(false));
    axios.get(`${API_BASE_URL}/api/manager/team-profiles`)
      .then(res => setProfiles(res.data))
      .catch(() => setProfiles(null))
      .finally(() => setProfLoading(false));
  }, [currentUser?.role]);

  const kpis     = data?.kpis     || {};
  const insights = data?.insights || [];

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 pt-20 pb-24 md:pt-24 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentUser ? `Hi, ${(currentUser.full_name || currentUser.email || '').split('@')[0].split(' ')[0]}` : 'Dashboard'}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <button
            onClick={() => fetchDashboard(true)}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900
                       bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow
                       disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            <AlertTriangle size={14} className="inline mr-2" />
            {error}
          </div>
        )}

        {/* ── AI Insights ─────────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-violet-100 flex items-center justify-center">
                <Zap size={12} className="text-violet-600" />
              </div>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                AI Insights
              </h2>
            </div>
            {data?.generated_at && !loading && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Clock size={10} />
                <RelativeTime iso={data.generated_at} />
                {data.is_cached && <span className="ml-1 text-gray-300">· cached</span>}
              </span>
            )}
          </div>

          {loading && !refreshing ? (
            <InsightSkeleton />
          ) : refreshing ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-400">
              <Loader2 size={24} className="animate-spin" />
              <p className="text-sm">AI is assembling your insights…</p>
            </div>
          ) : insights.length === 0 ? (
            <div className="rounded-xl bg-gray-100 border border-gray-200 p-6 text-center text-sm text-gray-500">
              No insights available. Click Refresh to generate.
            </div>
          ) : (
            <div className="space-y-3">
              {insights
                .slice()
                .sort((a, b) => a.priority - b.priority)
                .map((card, i) => (
                  <InsightCard
                    key={i}
                    card={card}
                    onAction={view => onViewChange && onViewChange(view)}
                  />
                ))}
            </div>
          )}
        </section>

        {/* ── Performance KPIs ─────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 rounded-md bg-emerald-100 flex items-center justify-center">
              <Target size={12} className="text-emerald-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {isManager(currentUser?.role) ? 'Team Performance' : 'Performance'}
            </h2>
          </div>

          {lastKpiUpdate && !loading && (
            <p className="text-[10px] text-gray-400 mb-2 flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live · updated <RelativeTime iso={lastKpiUpdate.toISOString()} />
            </p>
          )}

          {loading ? (
            <KPISkeleton />
          ) : isManager(currentUser?.role) ? (
            /* ── Manager KPI grid ─────────────────────────────── */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KPICard
                label="Team Size"
                value={kpis.rep_count ?? '—'}
                sub="active sales reps"
                color="blue"
                icon={Users}
              />
              <KPICard
                label="Active Today"
                value={kpis.active_reps_today ?? '—'}
                sub={`${kpis.team_activity_pct ?? 0}% of team`}
                color={
                  (kpis.team_activity_pct ?? 0) >= 70 ? 'green' :
                  (kpis.team_activity_pct ?? 0) >= 40 ? 'amber' : 'red'
                }
                icon={UserCheck}
              />
              <KPICard
                label="Visits This Week"
                value={kpis.team_visits_this_week ?? '—'}
                sub="team completed"
                color="green"
                icon={CheckCircle2}
              />
              <KPICard
                label="Visits This Month"
                value={kpis.team_visits_this_month ?? '—'}
                sub="team completed"
                color="purple"
                icon={BarChart2}
              />
              <KPICard
                label="Inactive Reps"
                value={kpis.inactive_reps ?? '—'}
                sub="no visit in 7 days"
                color={kpis.inactive_reps > 0 ? 'red' : 'gray'}
                icon={UserX}
              />
              <KPICard
                label="Leads at Risk"
                value={kpis.team_leads_at_risk ?? '—'}
                sub="45 days unvisited"
                color={kpis.team_leads_at_risk > 5 ? 'red' : kpis.team_leads_at_risk > 0 ? 'amber' : 'gray'}
                icon={AlertTriangle}
              />
              <KPICard
                label="Unassigned"
                value={kpis.unassigned_leads ?? '—'}
                sub="leads need rep"
                color={kpis.unassigned_leads > 5 ? 'amber' : 'gray'}
                icon={MapPin}
              />
              <KPICard
                label="Won This Month"
                value={kpis.team_won_this_month ?? '—'}
                sub="team closes"
                color={kpis.team_won_this_month > 0 ? 'green' : 'gray'}
                icon={Award}
              />
            </div>
          ) : (
            /* ── Salesperson KPI grid ─────────────────────────── */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KPICard
                label="Visits Today"
                value={kpis.visits_today ?? '—'}
                sub="planned & active"
                color="blue"
                icon={Calendar}
              />
              <KPICard
                label="This Week"
                value={kpis.visits_this_week ?? '—'}
                sub="completed visits"
                color="green"
                icon={CheckCircle2}
              />
              <KPICard
                label="Monthly Goal"
                value={`${kpis.monthly_progress_pct ?? 0}%`}
                sub={`${kpis.visits_this_month ?? 0} / ${kpis.monthly_target ?? 20} visits`}
                color={
                  (kpis.monthly_progress_pct ?? 0) >= 75 ? 'green' :
                  (kpis.monthly_progress_pct ?? 0) >= 40 ? 'amber' : 'red'
                }
                icon={Target}
              />
              <KPICard
                label="At Risk"
                value={kpis.leads_at_risk ?? '—'}
                sub="leads near auto-release"
                color={kpis.leads_at_risk > 0 ? 'red' : 'gray'}
                icon={AlertTriangle}
              />
              <KPICard
                label="New Leads"
                value={kpis.new_leads_week ?? '—'}
                sub="assigned this week"
                color="purple"
                icon={Users}
              />
              <KPICard
                label="Coverage"
                value={`${kpis.coverage_pct ?? 0}%`}
                sub="territory last 30 days"
                color={
                  (kpis.coverage_pct ?? 0) >= 60 ? 'green' :
                  (kpis.coverage_pct ?? 0) >= 30 ? 'amber' : 'gray'
                }
                icon={MapPin}
              />
            </div>
          )}
        </section>

        {/* ── Won This Month ───────────────────────────────────────────────── */}
        {!loading && !isManager(currentUser?.role) && (kpis.won_this_month ?? 0) > 0 && (
          <section>
            <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white">
              <div className="flex items-center gap-3">
                <Award size={24} className="opacity-80 flex-shrink-0" />
                <div>
                  <p className="font-semibold">
                    {kpis.won_this_month} {kpis.won_this_month === 1 ? 'lead' : 'leads'} won this month
                  </p>
                  <p className="text-sm text-emerald-100 mt-0.5">Keep it up — great momentum this month.</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Manager Team Won Banner ──────────────────────────────────────── */}
        {!loading && isManager(currentUser?.role) && (kpis.team_won_this_month ?? 0) > 0 && (
          <section>
            <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white">
              <div className="flex items-center gap-3">
                <Award size={24} className="opacity-80 flex-shrink-0" />
                <div>
                  <p className="font-semibold">
                    {kpis.team_won_this_month} team {kpis.team_won_this_month === 1 ? 'close' : 'closes'} this month
                  </p>
                  <p className="text-sm text-emerald-100 mt-0.5">Great team momentum — keep the pipeline moving.</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Team Coverage ────────────────────────────────────────────────── */}
        {isManager(currentUser?.role) && profiles?.reps?.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-indigo-100 flex items-center justify-center">
                  <GitMerge size={12} className="text-indigo-600" />
                </div>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Team Profiles
                </h2>
              </div>
              {profiles?.team_cohesion != null && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  Cohesion
                  <span className="font-semibold text-indigo-600">
                    {Math.round(profiles.team_cohesion * 100)}%
                  </span>
                </span>
              )}
            </div>

            {profiles?.embedding_coverage < 1 && (
              <p className="text-xs text-gray-400 mb-3">
                {Math.round((profiles?.embedding_coverage || 0) * profiles?.reps?.length)}/
                {profiles?.reps?.length} reps have behaviour profiles.
                Profiles build automatically as reps visit leads.
              </p>
            )}

            {profLoading ? (
              <ProfileSkeleton />
            ) : (
              <div className="space-y-2">
                {profiles.reps.map(rep => (
                  <RepProfileRow key={rep.user_id} rep={rep} />
                ))}
              </div>
            )}
          </section>
        )}

        {isManager(currentUser?.role) && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center">
                  <MapPin size={12} className="text-blue-600" />
                </div>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Team Coverage
                </h2>
              </div>
              {coverage?.gaps && (
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  {coverage.gaps.uncovered_leads > 0 && (
                    <span className="flex items-center gap-1">
                      <AlertTriangle size={10} className="text-amber-500" />
                      {coverage.gaps.uncovered_leads} uncovered leads
                    </span>
                  )}
                  {coverage.gaps.overlap_cells > 0 && (
                    <span className="text-gray-400">
                      {coverage.gaps.overlap_cells} overlap cells
                    </span>
                  )}
                </div>
              )}
            </div>

            {covLoading ? (
              <CoverageSkeleton />
            ) : !coverage?.reps?.length ? (
              <div className="rounded-xl bg-gray-100 border border-gray-200 p-4 text-center text-sm text-gray-500">
                No rep data available.
              </div>
            ) : (
              <div className="space-y-2">
                {coverage.reps.map(rep => (
                  <RepCoverageRow key={rep.user_id} rep={rep} />
                ))}
              </div>
            )}
          </section>
        )}

      </div>
    </div>
  );
};

export default DashboardView;
